"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SessionManager = void 0;
/**
 * Framework-agnostic owner of auth state.
 *
 * Two subscription layers exist intentionally:
 *   - LAYER 1: `adapter.subscribeToSignInRedirectComplete()` — wraps the
 *     SDK's event bus (Amplify Hub). SessionManager registers itself here
 *     in the constructor so it learns when the OAuth flow completes.
 *   - LAYER 2: `sessionManager.subscribe()` — state changes from ANY cause
 *     (sign-in complete, sign-out, manual refresh, etc.). External consumers
 *     (React AuthProvider, analytics, multi-tab sync) use this.
 *
 * Lifecycle:
 *   1. Construct: `new SessionManager(adapter)` — auto-probes the existing
 *      session via refresh() inside the constructor.
 *   2. Use `signIn()` / `signOut()` for user-initiated actions.
 *   3. Call `refresh()` manually only if you need to force a re-check
 *      (e.g. window-focus events). Route guards don't need to call it —
 *      the AuthProvider re-runs refresh() on every location change.
 */
class SessionManager {
    constructor(adapter) {
        this.state = {
            isLoggedIn: false,
            // Defaults to false. signIn() flips this on for the instant before
            // the browser redirects to Cognito, and the Hub callback flips it
            // on while the post-OAuth refresh is in flight. Defaulting to true
            // caused LoginScreen to flash a spinner for one frame at /login boot
            // before the initial probe resolved to "not authenticated."
            isValidatingSession: false,
            error: null,
        };
        this.listeners = new Set();
        this.unsubFromRedirect = null;
        // In-flight refresh() dedup. Concurrent callers wait on the same
        // promise instead of each doing their own probe. Guards against:
        //   - Constructor's `void this.refresh()` racing with the Hub
        //     bridge's `handleSignInRedirectComplete` on OAuth callback
        //     (?code= in URL fires BOTH within the same tick)
        //   - AuthProvider's route-change effect racing with the initial
        //     probe if a fast SPA navigation happens post-Cognito redirect
        //   - Any future caller (visibilitychange listener, manual retry
        //     button, etc.) hitting a still-running refresh
        this.refreshInFlight = null;
        this.adapter = adapter;
        // LAYER 1: register an internal handler with the adapter's Hub bridge.
        // When the OAuth redirect completes, the adapter normalizes the Hub
        // payload into a SignInRedirectResult and calls this handler. The
        // handler then updates our state, which fires LAYER-2 listeners.
        this.unsubFromRedirect = this.adapter.subscribeToSignInRedirectComplete(result => this.handleSignInRedirectComplete(result));
        // Auto-initialize: synchronously mark "checking" so consumers reading
        // `useAuth()` at first render see "we're validating" rather than "not
        // authenticated". Without this, a route guard gated on `isLoggedIn`
        // would redirect away during an OAuth callback (`?code=...`) before
        // Amplify had a chance to process the URL — losing the code and
        // stranding the user on /login. refresh() runs async; fetchAuthSession
        // inside it coordinates with any in-flight Amplify code exchange,
        // and the result lands as a normal state update via set().
        this.state = Object.assign(Object.assign({}, this.state), { isValidatingSession: true });
        void this.refresh();
    }
    /** Read the current state synchronously. */
    getState() {
        return this.state;
    }
    /**
     * LAYER 2: subscribe to state changes.
     *
     * The listener is called immediately with the current state (so subscribers
     * don't have to call `getState()` separately to seed themselves), then on
     * every subsequent state change. Returns an `Unsubscribe` function that
     * removes the listener.
     */
    subscribe(listener) {
        this.listeners.add(listener);
        listener(this.state);
        return () => {
            this.listeners.delete(listener);
        };
    }
    /** Re-check auth state by calling the adapter. Useful on route changes
     *  or window-focus events. The constructor auto-calls this once at boot
     *  so callers rarely need to invoke it manually. */
    refresh() {
        return __awaiter(this, void 0, void 0, function* () {
            // If a probe is already running, return the same promise. Prevents
            // wasted work when multiple triggers fire during the same window
            // (see refreshInFlight docs above for the concrete scenarios).
            if (this.refreshInFlight)
                return this.refreshInFlight;
            this.refreshInFlight = (() => __awaiter(this, void 0, void 0, function* () {
                this.set({ isValidatingSession: true });
                try {
                    const isAuthenticated = yield this.adapter.isAuthenticated();
                    this.set({
                        isLoggedIn: isAuthenticated,
                        isValidatingSession: false,
                        error: null,
                    });
                }
                catch (e) {
                    this.set({
                        isLoggedIn: false,
                        isValidatingSession: false,
                        error: e instanceof Error ? e.message : "Session check failed",
                    });
                }
            }))();
            try {
                yield this.refreshInFlight;
            }
            finally {
                this.refreshInFlight = null;
            }
        });
    }
    /** Kick off the Cognito Hosted UI sign-in flow (full-page redirect).
     *  This call typically never resolves on success — the browser navigates
     *  away. Success is delivered later via LAYER 1 → state change → LAYER 2. */
    signIn() {
        return __awaiter(this, void 0, void 0, function* () {
            this.set({ isValidatingSession: true, error: null });
            try {
                yield this.adapter.signInWithRedirect();
            }
            catch (e) {
                this.set({
                    isValidatingSession: false,
                    error: e instanceof Error ? e.message : "Sign-in failed",
                });
            }
        });
    }
    /** Sign out via the adapter and update state. */
    signOut() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.adapter.clearAuthSession();
            }
            finally {
                this.set({
                    isLoggedIn: false,
                    isValidatingSession: false,
                    error: null,
                });
            }
        });
    }
    /**
     * Opt-in federated sign-out. Use from user-initiated logout flows
     * (drawer / settings) when you want to also clear the identity
     * provider's session cookie so the next sign-in re-prompts for
     * credentials. On native this opens Cognito's /oauth2/logout URL in
     * the in-app browser; on web Amplify's regular sign-out is already
     * federated so this falls back to the local-only path.
     *
     * Do NOT call from force-logout paths. Use `signOut()` there — that
     * stays local-only so backend session expiries don't surface a
     * browser flash to users.
     */
    signOutFederated() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.adapter.signOutFederated) {
                    yield this.adapter.signOutFederated();
                }
                else {
                    yield this.adapter.clearAuthSession();
                }
            }
            finally {
                this.set({
                    isLoggedIn: false,
                    isValidatingSession: false,
                    error: null,
                });
            }
        });
    }
    /**
     * Read the current ID token. Returns `null` if unavailable.
     * Use this from HTTP interceptors to attach the Bearer header.
     */
    getToken() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const accessToken = yield this.adapter.getAccessToken();
                const token = yield accessToken.get(() => __awaiter(this, void 0, void 0, function* () { return undefined; }));
                return token !== null && token !== void 0 ? token : null;
            }
            catch (_a) {
                return null;
            }
        });
    }
    /**
     * Expose the underlying adapter for low-level operations like
     * `changePassword`, `subscribeToCrossTabAuthChanges`, etc. Prefer the
     * higher-level methods on SessionManager when one exists. Consumers
     * that only need HTTP-layer token access should prefer
     * `getAccessTokenProvider()` — it returns a narrower interface, keeping
     * the wide-adapter API out of module-scope wiring like `AdapterFactory`.
     */
    getAdapter() {
        return this.adapter;
    }
    /**
     * Narrow view of the underlying adapter — just enough for HTTP / socket
     * interceptors to fetch a bearer token. Returned reference IS the same
     * adapter instance (IAuthAdapter extends IAccessTokenProvider), typed
     * narrower so callers can't reach for auth-lifecycle methods they don't
     * need. Used by `AdapterFactory` in `packages/adapters` to keep the
     * auth-facing surface of the adapters package explicit.
     */
    getAccessTokenProvider() {
        return this.adapter;
    }
    /** Dispose of internal subscriptions. Most apps never need this (auth
     *  lives for the whole app's lifetime). */
    dispose() {
        var _a;
        (_a = this.unsubFromRedirect) === null || _a === void 0 ? void 0 : _a.call(this);
        this.unsubFromRedirect = null;
        this.listeners.clear();
    }
    handleSignInRedirectComplete(result) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (result.success) {
                yield this.refresh();
            }
            else {
                this.set({
                    isLoggedIn: false,
                    isValidatingSession: false,
                    error: (_a = result.error) !== null && _a !== void 0 ? _a : "Sign-in failed",
                });
            }
        });
    }
    /** Update internal state and notify Layer-2 listeners. No-op if the
     *  resulting state would be identical (skip pointless re-renders). */
    set(partial) {
        const next = Object.assign(Object.assign({}, this.state), partial);
        if (next.isLoggedIn === this.state.isLoggedIn &&
            next.isValidatingSession === this.state.isValidatingSession &&
            next.error === this.state.error) {
            return;
        }
        this.state = next;
        this.listeners.forEach(l => l(this.state));
    }
}
exports.SessionManager = SessionManager;
